import axios from 'axios';
import { installRequestId } from '../utils/requestId';
import { resolveMonolithApiBaseUrl, getMarketplaceDeployOrigin } from './resolveApiBaseUrl';

const API_BASE_URL = resolveMonolithApiBaseUrl([
  'REACT_APP_WALLET_API_URL',
  'REACT_APP_POINTS_API_URL',
  'REACT_APP_API_BASE_URL',
]);

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,  // Send cookies with requests (httpOnly cookies)
});
installRequestId(api);

// Request interceptor - cookies are sent automatically by browser
api.interceptors.request.use(
  (config) => {
    // Cookies are automatically sent when withCredentials: true
    // No need to manually add Authorization header since we're on the same domain
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        // Refresh token is in httpOnly cookie, sent automatically
        // Use same domain for token refresh to ensure cookies are sent
        const accountsApiUrl = process.env.REACT_APP_API_BASE_URL ||
          getMarketplaceDeployOrigin() || 'http://localhost:8001';
        await axios.post(
          `${accountsApiUrl}/api/token/refresh/`,
          {},  // No body needed - refresh token in cookie
          { withCredentials: true }  // Send cookies
        );
        // New tokens are set in httpOnly cookies by backend
        // Retry original request (cookies will be sent automatically)
        return api(originalRequest);
      } catch (refreshError) {
        // Only redirect to login if it's a persistent auth failure
        // Don't redirect on network errors or temporary issues
        if (refreshError.response?.status === 401 || refreshError.response?.status === 403) {
          // Redirect to login - cookies will be cleared by backend
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

export const pointsAPI = {
  /** Wallet id, balance, and recent activity (profile / wallet screen). */
  getWallet: async () => {
    const path = '/api/wallet';
    const response = await api.get(path);
    return response.data;
  },

  // Get points balance
  getBalance: async () => {
    const path = '/api/wallet/balance';
    const response = await api.get(path);
    return response.data;
  },

  // Get points transactions
  getTransactions: async () => {
    const path = '/api/wallet/transactions';
    const response = await api.get(path);
    return response.data;
  },

  // Transfer points (recipient verified by username + phone)
  transferPoints: async ({ username, phone_number, amount }) => {
    const response = await api.post('/api/wallet/transfer', {
      username,
      phone_number,
      amount,
    });
    return response.data;
  },

  // Initialize points (if needed)
  initializePoints: async () => {
    const response = await api.post('/api/wallet/initialize');
    return response.data;
  },

  getVouchers: async () => {
    const response = await api.get('/api/wallet/vouchers/');
    return response.data;
  },

  redeemVoucher: async (voucherType) => {
    const response = await api.post('/api/wallet/vouchers/redeem/', {
      voucher_type: voucherType,
    });
    return response.data;
  },
};

export default pointsAPI;

